import {
  Close as CloseIcon,
  EditOutlined as EditOutlinedIcon,
  OpenInFull as OpenInFullIcon,
} from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { GCFComponentLayout } from 'src/components/gcfComponentLayout';
import { PreviewModal } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { ScaleContainer } from 'src/components/scaleContainer/ScaleContainer';
import { PATHS } from 'src/constants/paths';
import { useAssemblyPreviewSidebar } from 'src/features/assemblies/listAssemblyPage/hooks/AssemblyPreviewSidebar.hook';
import { useGetAllAssemblyTags } from 'src/hooks/api/assemblyTags/useGetAllAssemblyTags';
import { IStatus } from 'src/interfaces';
import {
  generateAssemblyProductFilters,
  getLayoutTypeName,
  lang,
  useUserRole,
} from 'src/utils';

import type { AssemblyPreviewSidebarProps } from './AssemblyPreviewSidebar.interface';
import {
  AssemblyPreviewContainer,
  AssemblyPreviewTitleContainer,
  CardInfoDescription,
  CardInfoTitle,
  Container,
  StyledChipContainer,
  StyledDynamicTooltipChip,
} from './AssemblyPreviewSidebar.styled';

export const AssemblyPreviewSidebar = (props: AssemblyPreviewSidebarProps) => {
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const { userRole } = useContext(AuthDataProvider);

  const {
    assemblyComponentsDetails,
    assemblyInfoModal,
    assemblyComponents,
    layouts,
    onHandleModalClose,
    onHandleModalOpen,
    hasModalPreviewLoaded,
    isLoadingModalPreview,
  } = useAssemblyPreviewSidebar(props?.highlightedAssembly);
  const showEditButton = !isViewOnly;
  const { data: selectedChipTags = [] } = useGetAllAssemblyTags(
    userRole,
    [`id.list=${(props.highlightedAssembly?.assembly_tag_id ?? []).join('|')}`],
    Boolean((props.highlightedAssembly?.assembly_tag_id?.length ?? 0) > 0),
  );
  const createdAt = assemblyComponentsDetails?.created_at
    ? dayjs(assemblyComponentsDetails?.created_at).format('MM/DD/YYYY')
    : '';

  const { productFilters, productVariationFilters } =
    generateAssemblyProductFilters(
      assemblyComponentsDetails?.assemblyComponents ?? [],
      assemblyComponentsDetails?.components ?? [],
    );

  if (!props?.highlightedAssembly) return null;

  return (
    <>
      <Container spacing={4}>
        {!props?.hideSidebarPreviewButton && (
          <AssemblyPreviewTitleContainer>
            <Button
              variant="outlined"
              onClick={onHandleModalOpen}
              startIcon={<OpenInFullIcon />}>
              {lang.expand}
            </Button>
            <IconButton aria-label="close" onClick={props.onClose}>
              <CloseIcon />
            </IconButton>
          </AssemblyPreviewTitleContainer>
        )}
        <AssemblyPreviewContainer container>
          {props.isLoadingSidebarPreview ? (
            <Progress />
          ) : (
            <>
              {props.hasSidebarPreviewLoaded && (
                <ScaleContainer
                  containerStyle={{
                    height: '100%',
                    minWidth: '100%',
                  }}>
                  <GCFComponentLayout
                    fullSize
                    layout={layouts[0]}
                    assemblyComponents={assemblyComponents}
                    productFilters={productFilters}
                    productVariationFilters={productVariationFilters}
                    skipProductsApiOnEmptyFilters={
                      props.skipProductsApiOnEmptyFilters
                    }
                  />
                </ScaleContainer>
              )}
            </>
          )}
        </AssemblyPreviewContainer>
        <CardInfoTitle>{props.highlightedAssembly.name}</CardInfoTitle>

        <Stack direction="row" spacing={2}>
          <Box flex={1}>
            <Typography fontWeight={400}>{lang.dateCreated}</Typography>
            <Typography>{createdAt}</Typography>
          </Box>

          <Box flex={1}>
            <Typography fontWeight={400}>{lang.layoutType}</Typography>
            <Typography>
              {getLayoutTypeName(props.highlightedAssembly)}
            </Typography>
          </Box>
        </Stack>
        {!props.hideSidebarPreviewButton && (
          <>
            {selectedChipTags.length > 0 && (
              <CardInfoDescription>
                <Stack>
                  <Typography fontWeight={400}>{lang.tags}</Typography>
                  <Box overflow={'auto'} maxHeight={200}>
                    {selectedChipTags.map((tag) => (
                      <StyledChipContainer key={tag.id}>
                        <StyledDynamicTooltipChip
                          chipName={tag?.name}
                          tooltipTitle={tag?.name}
                          tooltipWidth={500}
                          tooltipActivationWidth={252}
                        />
                      </StyledChipContainer>
                    ))}
                  </Box>
                </Stack>
              </CardInfoDescription>
            )}
            <Stack direction="row" spacing={2}>
              <Button
                sx={{ flex: 1 }}
                variant={isViewOnly ? 'contained' : 'outlined'}
                onClick={() =>
                  navigate(
                    PATHS.ASSEMBLY_DETAILS(props.highlightedAssembly?.id!),
                  )
                }>
                {lang.viewDetails}
              </Button>

              {showEditButton && (
                <Button
                  sx={{ flex: 1 }}
                  startIcon={<EditOutlinedIcon />}
                  disabled={
                    props.hasStageAssembly ||
                    assemblyComponentsDetails?.status === IStatus.READY
                  }
                  onClick={() =>
                    navigate(
                      PATHS.ASSEMBLY_EDIT(props.highlightedAssembly?.id!),
                    )
                  }>
                  {lang.edit}
                </Button>
              )}
            </Stack>
          </>
        )}
      </Container>

      {assemblyInfoModal.open && (
        <PreviewModal
          open={assemblyInfoModal.open}
          onClose={onHandleModalClose}
          layout={layouts[0]}
          assemblyComponents={assemblyComponents}
          type="assembly"
          content={props.highlightedAssembly}
          hasModalPreviewLoaded={hasModalPreviewLoaded}
          isLoadingModalPreview={isLoadingModalPreview}
          hasStageAssembly={props.hasStageAssembly}
          productFilters={productFilters}
          productVariationFilters={productVariationFilters}
          skipProductsApiOnEmptyFilters={true}
        />
      )}
    </>
  );
};
